import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Popover, Row, Space, Table } from "antd";
import {
  CloudDownloadOutlined,
  EyeFilled,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import { RootState } from "../../../shared/types/common";
import { getStaffAssignmentSummary } from "../../../core/store/reducers/project.slice";
import { formatCost } from "../../../shared/utils/utils";
import moment from "moment";
import dayjs from "dayjs";
import Papa from "papaparse";
import { Client } from "../../../core/api-client";
import {
  API_GET_ALL_ACCOUNTS_LIST,
  API_GET_ALL_PROJECT_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_PROJECT_DETAILS,
} from "../../../shared/routes/private.routes";
import { TABLE_ROWS_COUNT } from "../../../shared/constants";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import DataTableComponent from "../../../shared/components/data-table/data-table.component";

const StaffAssignmentSummaryComponent: React.FC = () => {
  const [form] = useForm();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState<any>({});
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [billingCycleData, setBillingCycleData] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [accountList, setAccountList] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [showProjectField, setShowProjectField] = useState<boolean>(false); // State for controlling visibility of Project field
  const [showAccountField, setShowAccountField] = useState<boolean>(false); // State for controlling visibility of Account field
  const [showStaffField, setShowStaffField] = useState<boolean>(false);

  const toMomentRange = (range: any): [moment.Moment, moment.Moment] | undefined => {
    if (!range || range.length !== 2) return undefined;
    const start = moment(range[0]?.toDate ? range[0].toDate() : range[0]);
    const end = moment(range[1]?.toDate ? range[1].toDate() : range[1]);
    if (!start.isValid() || !end.isValid()) return undefined;
    return [start, end];
  };

  const toPickerRange = (range: [moment.Moment, moment.Moment]) => [
    dayjs(range[0].toDate()),
    dayjs(range[1].toDate()),
  ];

  useEffect(() => {
    loadList(); 
  }, [filters]);

  const loadList = async () => {
    const filter: any = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== "" && value !== undefined && value !== null)
    );

    const result = await dispatch(getStaffAssignmentSummary({ body: filter }));
    const data = result?.payload?.data?.data;
    console.log("API Response:", data);
    if (data?.length > 0) {
      const groupedData: Record<string, any> = {};
      const monthlyTotals: Record<string, number> = {};
      let totalMonthlyCost = 0;
      const selectedMonthRange = filter.month;

      data.forEach((item: any) => {
        item.allocations.forEach((alloc: any) => {
          const percentAllocation = Number(alloc.percentAllocation) || 0;
          if (percentAllocation <= 0) {
            return;
          }

          const uniqueKey = `${item.staffId}-${item.projectId || item.projectName}`;
          const monthKey = alloc.month;
          if (!isMonthInSelectedRange(monthKey, selectedMonthRange)) {
            return;
          }

          if (!groupedData[uniqueKey]) {
            groupedData[uniqueKey] = {
              key: uniqueKey,
              staffId: item.staffId,
              staffName: item.staffName,
              employeeId: item.employeeId,
              projectName: item.projectName,
              accountName: item.accountName,
              designation: item?.designation,
              // ctc: item.ctc ? formatCost1(Math.round(item.ctc / 12),item.salaryCurrency) : 0,
              totalCost: 0,
            };
          }

          // Save percent allocation for that month
          if (!groupedData[uniqueKey][monthKey + "_data"]) {
            groupedData[uniqueKey][monthKey + "_data"] = [];
          }
          groupedData[uniqueKey][monthKey] = groupedData[uniqueKey][monthKey]
            ? groupedData[uniqueKey][monthKey] + percentAllocation
            : percentAllocation;
          groupedData[uniqueKey][monthKey + "_data"]?.push({
            ...alloc,
            percentAllocation,
            projectName: item.projectName,
            sowNumber: item.sowNumber,
          });
        });
      });

      // Convert grouped data to array
      const updatedData = Object.values(groupedData);

      // // Add total row
      // const totalRow = {
      //   key: 'total',
      //   staffName: '',
      //   projectName: '',
      //   ...monthlyTotals,
      //   totalCost: totalMonthlyCost,
      // };

      // updatedData.push(totalRow);

      setBillingCycleData(updatedData);      
      updateColumns(filter.month, updatedData);        
            
    } else {
      setBillingCycleData([]);
    }
  };

  const isMonthInSelectedRange = (
    month: string,
    monthsRange?: [moment.Moment, moment.Moment]
  ) => {
    if (!monthsRange || monthsRange.length !== 2) {
      return true;
    }

    const assignmentMonth = moment(month, ["YYYY-MM", "YYYY-M"], true);
    if (!assignmentMonth.isValid()) {
      return false;
    }

    return assignmentMonth.isBetween(
      monthsRange[0].clone().startOf("month"),
      monthsRange[1].clone().startOf("month"),
      "month",
      "[]"
    );
  };

  const exportToCSV = () => {
    const monthsRange = toMomentRange(form.getFieldValue("month"));
    if (!monthsRange || monthsRange.length !== 2) return;

    const startMonth = monthsRange[0].clone().startOf("month");
    const endMonth = monthsRange[1].clone().endOf("month");

    // Generate all months in the range
    const monthColumns: string[] = [];
    let currentMonth = startMonth.clone();
    while (currentMonth.isSameOrBefore(endMonth)) {
      monthColumns.push(currentMonth.format("YYYY-MM"));
      currentMonth.add(1, "month");
    }

    // Map table rows into CSV rows
    const csvData = billingCycleData.map((row) => {
      const rowData: Record<string, string | number> = {
        "Emp ID": row.employeeId || "",
        "Staff Name": row.staffName || "",
        Designation: row.designation || "",
        // "CTC": row.ctc || "",
        Client: row.accountName || "",
        Project: row.projectName || "",
      };

      // Add month-wise allocations
      monthColumns.forEach((month) => {
        rowData[month] = row[month] ? `${row[month]}%` : "";
      });

      return rowData;
    });

    // Convert to CSV & download
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "billing_summary.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onFilterApply = async (values: any) => {
    const monthRange = toMomentRange(values.month);
    const normalizedValues = {
      ...values,
      month: monthRange,
    };
    setFilters(normalizedValues);
    if (normalizedValues.month !== undefined) {
          updateColumns(normalizedValues.month);
          console.log("Filters applied:", normalizedValues);
        }
    
   
    if (normalizedValues.projectId) {
      await fetchProjectDetails(normalizedValues.projectId);
    }
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };

  const editEntityListener = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  const fetchProjectDetails = async (projectId: string) => {
    try {
      const response = await Client.get(
        API_GET_PROJECT_DETAILS + `${projectId}`,
      );
      setProjectDetails(response?.data?.data[0] || {});
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  useEffect(() => {
    fetchProjectList();
    fetchAccountList();
    fetchStaffList();
  }, []);

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  const fetchAccountList = async () => {
    const response = await Client.get(API_GET_ALL_ACCOUNTS_LIST);
    if (response?.data?.data) {
      setAccountList(response?.data?.data);
    } else {
      setAccountList([]);
    }
  };

  const fetchProjectList = async () => {
    try {
      const response = await Client.get(API_GET_ALL_PROJECT_LIST);
      if (response?.data?.data) {
        setProjectList(response?.data?.data);
      } else {
        setProjectList([]);
      }
    } catch (error) {
      console.error("Error fetching project list:", error);
    }
  };

  const projectDropdownOptions = projectList.map((project) => ({
    value: project?.projectId,
    label: project?.l2ProjectName + " (" + project?.sowNumber + ")",
  }));
  const accountDropdownOptions = accountList.map((account) => ({
    value: account?.accountId,
    label: account?.accountName + " - " + account?.entityName,
  }));

  const staffDropdownOptions = staffList.map((staff) => ({
    value: staff?.staffId,
    label: `${staff?.staffName} (${staff?.employeeId})`,
  }));

  const formProps = {
    form: form,
    name: "billing-cycle-summary",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "filterType",
        label: "",
        placeholder: "Filter Type",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 }, // Adjust column span
        showSearch: true,
        allowClear: true,
        options: [
          { value: "account", label: "Client" },
          { value: "project", label: "Project" },
          { value: "staff", label: "Staff" },
        ],
      },
      showProjectField && {
        name: "projectId",
        label: "",
        placeholder: "Project (SOW Number)",
        type: "Select",
        mode: "multiple",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 }, // Adjust column span
        showSearch: true,
        allowClear: true,
        options: projectDropdownOptions,
      },
      showAccountField && {
        name: "accountId",
        label: "",
        placeholder: "Client",
        type: "Select",
        mode: "multiple",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 }, // Adjust column span
        showSearch: true,
        allowClear: true,
        options: accountDropdownOptions,
      },
      showStaffField && {
        name: "staffId",
        label: "",
        placeholder: "Staff",
        type: "Select",
        mode: "multiple",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 }, // Adjust column span
        showSearch: true,
        allowClear: true,
        options: staffDropdownOptions,
      },
      {
        name: "month",
        placeholder: "Month",
        type: "RangePicker",
        colSpanResponsive: { xs: 12, sm: 6, md: 6, lg: 6 }, // Adjust column span
        showSearch: true,
        allowClear: true,
      },
    ].filter(Boolean), // To remove `false` values when `showProjectField` or `showAccountField` is false
    initialValues: {
      billingCycleName: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: (value: any, allValues: any) => {
      if ("filterType" in value) {
        if (value.filterType === "project") {
          setShowProjectField(true);
          setShowAccountField(false);
          setShowStaffField(false);
          form.setFieldsValue({ projectId: undefined }); // Clear vendor field when not applicable
        } else if (value.filterType === "account") {
          setShowAccountField(true);
          setShowProjectField(false);
          setShowStaffField(false);
          form.setFieldsValue({ accountId: undefined }); // Clear vendor field
        } else if (value.filterType === "staff") {
          setShowStaffField(true);
          setShowProjectField(false);
          setShowAccountField(false);
          form.setFieldsValue({ staffId: undefined }); // Clear vendor field
        } else {
          setShowProjectField(false);
          setShowAccountField(false);
          form.setFieldsValue({
            projectId: undefined,
            accountId: undefined,
            staffId: undefined,
          });
        }
      }
    },
  };

 
const updateColumns = (
  monthsRange?: [moment.Moment, moment.Moment],
  data: any[] = []
) => {
  let monthList: string[] = [];

  // Generate months from selected filter
  if (monthsRange && monthsRange.length === 2) {
    let currentMonth = monthsRange[0].clone().startOf("month");
    const endMonth = monthsRange[1].clone().endOf("month");

    while (currentMonth.isSameOrBefore(endMonth)) {
      monthList.push(currentMonth.format("YYYY-MM"));
      currentMonth.add(1, "month");
    }
  } else {
    // Generate months from API response
    monthList = [
      ...new Set(
        data.flatMap((row: any) =>
          Object.keys(row).filter((key) => /^\d{4}-\d{2}$/.test(key))
        )
      ),
    ].sort();
  }

  const monthColumns = monthList.map((month) => ({
    title: moment(month, "YYYY-MM").format("MMM 'YY"),
    dataIndex: month,
    key: month,
    sorter: false,
    width: "100px",
    render: (text: any, record: any) =>
      text ? (
        <>
          {text}%{" "}
          <Popover
            trigger="click"
            overlayStyle={{ width: "500px" }}
            content={
              <Table
                pagination={false}
                columns={[
                  {
                    title: "Project Name",
                    dataIndex: "projectName",
                  },
                  {
                    title: "SOW Number",
                    dataIndex: "sowNumber",
                  },
                  {
                    title: "% Allocation",
                    dataIndex: "percentAllocation",
                  },
                ]}
                dataSource={record[month + "_data"] || []}
              />
            }
          >
            <EyeFilled
              style={{
                color: "#666",
                marginLeft: 7,
                cursor: "pointer",
              }}
            />
          </Popover>
        </>
      ) : (
        ""
      ),
  }));

  setColumns([
    {
      title: "Emp. ID",
      dataIndex: "employeeId",
      key: "employeeId",
      fixed: true,
    },
    {
      title: "Staff Name",
      dataIndex: "staffName",
      key: "staffName",
      fixed: true,
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      width: "150px",
    },
    {
      title: "Client",
      key: "accountName",
      width: "150px",
      render: (_: any, record: any) => record.accountName,
    },
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
    },
    ...monthColumns,
  ]);
};

  const handleEdit = async (record: any, month: string) => {
    const staffId = record?.staffId;
    console.log(`Edit clicked for billingId: ${staffId}, month: ${month}`);
    const data = {
      page: 1,
      limit: TABLE_ROWS_COUNT,
    };
    // let result = await dispatch(
    //   getBillingCycleList({ params: data, body: { staffId: [staffId] ,month: month} })
    // );
    // console.log(result,'billingData')
    // const billingData = result?.payload?.data?.data[0];
    // editEntityListener(billingData);
  };

  // Define the columns for the details table
  const detailsColumns = [
    {
      title: "",
      dataIndex: "detail",
      key: "detail",
    },
    {
      title: "",
      dataIndex: "value",
      key: "value",
    },
  ];

  const totalImplementationCost = projectDetails
    ? (projectDetails.poCost * 100) /
        (parseFloat(projectDetails.margin) + 100) || ""
    : "";

  const actualResourceCost =
    billingCycleData.length > 0
      ? billingCycleData[billingCycleData.length - 1].totalCost
      : "";

  const profitLoss =
    totalImplementationCost !== "" && actualResourceCost !== ""
      ? totalImplementationCost - actualResourceCost
      : "";

  // Prepare the data for the details table
  const detailsData = projectDetails
    ? [
        {
          key: "poCost",
          detail: "PO Cost",
          value: formatCost(projectDetails.poCost) || "",
        },
        {
          key: "sst",
          detail: "SST(%)",
          value: projectDetails.sst || "",
        },
        {
          key: "markup",
          detail: "Markup (%)",
          value: projectDetails.margin || "",
        },
        {
          key: "implementationCost",
          detail: "Implementation Cost",
          value: formatCost(Number(totalImplementationCost)) || "",
        },
        // {
        //   key: "implementationCost",
        //   detail: "Implementation Cost",
        //   value: formatCost(Number(totalImplementationCost)) || "",
        // },
        {
          key: "actualResourceCost",
          detail: "Actual Resource Cost",
          value: formatCost(Number(actualResourceCost)) || "",
        },
        {
          key: "profit/loss",
          detail: "Profit/Loss",
          value: formatCost(Number(profitLoss)) || "",
        },
      ]
    : [];

  const calculateDateRange = (months: number) => {
    const endDate = moment();
    const startDate = moment().subtract(months - 1, "months");
    return { startDate, endDate };
  };

  const handleDateFilter = async (months: number) => {
    const { startDate, endDate } = calculateDateRange(months);
    let projectId = form.getFieldValue("projectId");
    let accountId = form.getFieldValue("accountId");
    if (projectId) {
      form.setFieldValue("projectId", projectId);
      await fetchProjectDetails(projectId);
    }
    if (accountId) {
      form.setFieldValue("accountId", accountId);
    }
    const monthRange: [moment.Moment, moment.Moment] = [startDate, endDate];
    setFilters({ month: monthRange, projectId, accountId });
    form.setFieldValue("month", toPickerRange(monthRange));
  };
  const calculateCurrentFinancialYear = () => {
    const currentMonth = moment().month();
    let startDate, endDate;

    // Assuming the financial year starts on April 1
    if (currentMonth >= 3) {
      // April or later
      startDate = moment().startOf("year").add(3, "months"); // April 1 of the current year
      endDate = moment()
        .add(1, "year")
        .startOf("year")
        .add(2, "months")
        .endOf("month"); // March 31 of the next year
    } else {
      // Before April (Jan-Mar)
      startDate = moment().subtract(1, "year").startOf("year").add(3, "months"); // April 1 of the previous year
      endDate = moment().startOf("year").add(2, "months").endOf("month"); // March 31 of the current year
    }

    return { startDate, endDate };
  };

  const handleFinancialYearFilter = async () => {
    const { startDate, endDate } = calculateCurrentFinancialYear();
    let projectId = form.getFieldValue("projectId");
    let accountId = form.getFieldValue("accountId");
    if (projectId) {
      form.setFieldValue("projectId", projectId);
      await fetchProjectDetails(projectId);
    }
    if (accountId) {
      form.setFieldValue("accountId", accountId);
    }
    const monthRange: [moment.Moment, moment.Moment] = [startDate, endDate];
    setFilters({ month: monthRange, projectId, accountId });
    form.setFieldValue("month", toPickerRange(monthRange));
  };

  const calculatePreviousFinancialYear = () => {
    console.log("Calculating Previous Financial Year"); // Check if this function is triggered
    let startDate, endDate;

    startDate = moment().subtract(1, "year").startOf("year").add(3, "months"); // April 1 of the previous year
    endDate = moment().startOf("year").add(2, "months").endOf("month"); // March 31 of the current year

    console.log("Calculated Dates:", { startDate, endDate }); // Log the calculated dates
    return { startDate, endDate };
  };
  const handlePreviousFinancialYearFilter = async () => {
    const { startDate, endDate } = calculatePreviousFinancialYear();
    console.log("Previous Financial Year:", { startDate, endDate });
    let projectId = form.getFieldValue("projectId");
    let accountId = form.getFieldValue("accountId");
    if (projectId) {
      form.setFieldValue("projectId", projectId);
      await fetchProjectDetails(projectId);
    }
    if (accountId) {
      form.setFieldValue("accountId", accountId);
    }
    const monthRange: [moment.Moment, moment.Moment] = [startDate, endDate];
    setFilters({ month: monthRange, projectId, accountId });
    form.setFieldValue("month", toPickerRange(monthRange));
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      <Button type="primary" onClick={() => handleDateFilter(3)}>
        <span className="font-size-14">Last 3 Mo</span>
      </Button>
      <Button type="primary" onClick={() => handleDateFilter(6)}>
        <span className="font-size-14">Last 6 Mo</span>
      </Button>
      <Button type="primary" onClick={() => handleDateFilter(12)}>
        <span className="font-size-14">Last 12 Mo</span>
      </Button>
      <Button type="primary" onClick={handleFinancialYearFilter}>
        <span className="font-size-14">Current F.Y</span>
      </Button>
      <Button type="primary" onClick={handlePreviousFinancialYearFilter}>
        <span className="font-size-14">Previous F.Y</span>
      </Button>
      <Button
        shape="circle"
        type="primary"
        icon={<CloudDownloadOutlined />}
        onClick={exportToCSV}
        title="Export CSV"
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <UserSwitchOutlined />
              </div>
              <h1 className="app-page-title">Staff Assignment Summary</h1>
            </div>
            <p className="app-page-subtitle">
              View staff allocation summary by project, client or staff
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>
      <div className="app-filter-bar">
        <div className="app-filter-title">Filters</div>
        <div className="table-filter-form">
          <GenericFormComponent {...formProps} />
        </div>
      </div>
      {billingCycleData.length > 0 && (
        <div className="app-table-card">
          <DataTableComponent
            pagination={false}
            loading={false}
            dataSource={billingCycleData}
            columns={columns}
            onPageChange={(e: any): void => {}}
            defaultPage={0}
            currentPage={0}
            totalRows={0}
            tableWidth="max-content"
            scrollY="calc(100vh - 360px)"
          />
        </div>
      )}
    </div>
  );
};

export default StaffAssignmentSummaryComponent;
